import { Bell, ArrowRightLeft, Bomb, UserPlus, AlertTriangle, Trophy, Megaphone, Swords, Clock, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { useNotifications } from '@/hooks/useNotifications';
import { useLeagueChat } from '@/hooks/useLeagueChat';
import ChatModal from '@/components/chat/ChatModal';
import { useState, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';

const notificationIcons: Record<string, typeof Bell> = {
  trade_received: ArrowRightLeft,
  trade_accepted: ArrowRightLeft,
  trade_rejected: ArrowRightLeft,
  waiver_won: UserPlus,
  waiver_outbid: UserPlus,
  sabotage_received: Bomb,
  draft_pick: Trophy,
  injury_update: AlertTriangle,
  league_announcement: Megaphone,
  matchup_result: Swords,
  lineup_reminder: Clock,
  general: Bell,
};

const notificationColors: Record<string, string> = {
  trade_received: 'text-blue-400',
  trade_accepted: 'text-green-400',
  trade_rejected: 'text-red-400',
  waiver_won: 'text-green-400',
  waiver_outbid: 'text-orange-400',
  sabotage_received: 'text-damage',
  draft_pick: 'text-primary',
  injury_update: 'text-yellow-400',
  league_announcement: 'text-purple-400',
  matchup_result: 'text-primary',
  lineup_reminder: 'text-orange-400',
  general: 'text-muted-foreground',
};

const formatTimeAgo = (dateStr: string): string => {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return 'Just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
};

interface NotificationBellProps {
  dialogOpen?: boolean;
  onDialogOpenChange?: (open: boolean) => void;
}

const NotificationBell = ({ dialogOpen, onDialogOpenChange }: NotificationBellProps = {}) => {
  const { notifications, unreadCount, loading, markAsRead, markAllAsRead, clearAll } = useNotifications();
  const { totalUnread: chatUnread } = useLeagueChat();
  const [internalOpen, setInternalOpen] = useState(false);
  // MLB-102 — panel is opened via the bell icon, so default tab is Notifications
  // (the thing the badge says you have). Was 'chat', which orphaned the badge.
  const [activeTab, setActiveTab] = useState<'chat' | 'notifications'>('notifications');

  const open = dialogOpen !== undefined ? dialogOpen : internalOpen;
  const setOpen = onDialogOpenChange || setInternalOpen;

  const totalBadge = unreadCount + chatUnread;

  // Clear app badge and delivered notifications when panel opens.
  // Also reset the active tab to Notifications on each fresh open so closing
  // + reopening always lands on the bell's intended view (MLB-102).
  useEffect(() => {
    if (open) {
      setActiveTab('notifications');
      if (Capacitor.isNativePlatform()) {
        PushNotifications.removeAllDeliveredNotifications();
      }
    }
  }, [open]);

  return (
    <>
      <Button variant="ghost" size="icon" className="relative lg:hidden" onClick={() => setOpen(true)}>
        <Bell className="h-5 w-5" />
        {totalBadge > 0 && (
          <Badge
            className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-[10px] bg-damage border-0"
          >
            {totalBadge > 9 ? '9+' : totalBadge}
          </Badge>
        )}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        {/* pt-16 leaves clearance for shadcn's default close X (positioned absolute right-4 top-4
            with p-3 → 40x40 tap target per the dialog.tsx update) so it doesn't overlap the
            Notifications tab label/badge in the top-right. Button bottom is at y=56; pt-16=64 → 8px buffer. */}
        <DialogContent className="p-0 gap-0 pt-16 max-w-md w-[calc(100vw-2rem)] h-[85vh] flex flex-col">
          {/* Tab Toggle */}
          <div className="flex border-b border-border">
            <button
              onClick={() => setActiveTab('chat')}
              className={cn(
                "flex-1 flex items-center justify-center gap-1.5 py-3 text-sm font-semibold transition-colors relative",
                activeTab === 'chat'
                  ? 'text-primary'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              <MessageCircle className="w-4 h-4" />
              Chat
              {chatUnread > 0 && (
                <Badge className="h-4 min-w-[1rem] px-1 text-[9px] bg-damage border-0">
                  {chatUnread}
                </Badge>
              )}
              {activeTab === 'chat' && (
                <span className="absolute bottom-0 left-4 right-4 h-0.5 bg-primary rounded-full" />
              )}
            </button>
            <button
              onClick={() => setActiveTab('notifications')}
              className={cn(
                "flex-1 flex items-center justify-center gap-1.5 py-3 text-sm font-semibold transition-colors relative",
                activeTab === 'notifications'
                  ? 'text-primary'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              <Bell className="w-4 h-4" />
              Notifications
              {unreadCount > 0 && (
                <Badge className="h-4 min-w-[1rem] px-1 text-[9px] bg-damage border-0">
                  {unreadCount}
                </Badge>
              )}
              {activeTab === 'notifications' && (
                <span className="absolute bottom-0 left-4 right-4 h-0.5 bg-primary rounded-full" />
              )}
            </button>
          </div>

          {/* Chat Tab */}
          {activeTab === 'chat' && (
            <div className="flex-1 min-h-0">
              <ChatModal open={true} onOpenChange={() => {}} embedded />
            </div>
          )}

          {/* Notifications Tab */}
          {activeTab === 'notifications' && (
            <div className="flex-1 min-h-0 flex flex-col">
              {/* Actions bar */}
              {unreadCount > 0 && (
                <div className="flex items-center justify-end px-4 py-2 border-b border-border">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-xs h-auto py-1 px-2"
                    onClick={markAllAsRead}
                  >
                    Mark all read
                  </Button>
                </div>
              )}

              <ScrollArea className="flex-1" style={{ maxHeight: 'calc(80vh - 8rem)' }}>
                {loading ? (
                  <div className="flex items-center justify-center h-[200px] text-muted-foreground">
                    <p className="text-sm">Loading...</p>
                  </div>
                ) : notifications.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-[200px] text-muted-foreground">
                    <Bell className="h-8 w-8 mb-2 opacity-50" />
                    <p className="text-sm">No notifications</p>
                  </div>
                ) : (
                  <div className="divide-y divide-border">
                    {notifications.map((notification) => {
                      const Icon = notificationIcons[notification.type] || Bell;
                      const iconColor = notificationColors[notification.type] || 'text-muted-foreground';

                      return (
                        <button
                          key={notification.id}
                          className={cn(
                            "w-full flex items-start gap-3 p-4 text-left transition-colors hover:bg-muted/50",
                            !notification.read && "bg-muted/30"
                          )}
                          onClick={() => markAsRead(notification.id)}
                        >
                          <div className={cn("mt-0.5", iconColor)}>
                            <Icon className="h-5 w-5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className={cn(
                                "text-sm font-medium truncate",
                                !notification.read && "text-foreground",
                                notification.read && "text-muted-foreground"
                              )}>
                                {notification.title}
                              </p>
                              {!notification.read && (
                                <span className="h-2 w-2 rounded-full bg-primary flex-shrink-0" />
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                              {notification.message}
                            </p>
                            <p className="text-[10px] text-muted-foreground/70 mt-1">
                              {formatTimeAgo(notification.created_at)}
                            </p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </ScrollArea>

              {notifications.length > 0 && (
                <>
                  <Separator />
                  <div className="p-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full text-xs text-muted-foreground"
                      onClick={clearAll}
                    >
                      Clear all notifications
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default NotificationBell;
